/* eslint-disable max-lines */
import { forwardRef, useState, useImperativeHandle, useCallback, useRef, useMemo, useEffect } from 'react';
import { Drawer, StepProps, Steps } from 'antd';
import { Icon } from '@fuxi/eevee-icon';
import { SSE } from 'sse.js';
import lookie from 'lookie';
import { useGetState, useLockFn, useSetState, useUpdateEffect } from 'ahooks';
import { Button, Dialog } from 'antd-mobile';
import { useCheckPingTimeout, useShowTime } from '@/hooks';
import { isNewSoftwareVersion } from '@/utils';
import { CURRENT_ANDROID_ID, storagePrefixV2 } from '@/constant';
import service from '@/service';
import { Toast } from '../index';
import ButtonWithCountdown from './button-with-countdown';
import PlayDeviceDetecting from './play-device-detecting';
import DetectError, { DeviceErrorStatus } from './detect-error';
import cx from './index.module.scss';

interface Props {}

export interface VoiceDetectionRefAttrs {
  handleDetection: (deviceInfo: DeviceItem) => void;
}

type DetectStatus =
  /** 等待检测 */
  | 'Pending'
  /** 播放设备软件监测 */
  | 'DetectPlayDeviceSoftware'
  /** 播放设备硬件监测 */
  | 'DetectPlayDeviceHardware'
  /** 播放设备检测完成 */
  | 'PlayDeviceComplete'
  /** 等待麦克风设备检测 */
  | 'PrepareDetectMicDevice'
  /** 麦克风设备检测 */
  | 'DetectMicDevice'
  /** 完成麦克风设备检测 */
  | 'DetectMicDeviceComplete'
  /** 所有设备检测完成 */
  | 'AllDeviceDetectComplete';

const VOICE_WAVE_IMG = storagePrefixV2 + '/voice-detect/voice-wave.gif';

type AllButtonType = 'detectPlayDeviceSoftware' | 'detectMicDevice';

const VoiceDetection = forwardRef<VoiceDetectionRefAttrs, Props>((props, ref) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [deviceInfo, setDeviceInfo] = useState<DeviceItem>({} as DeviceItem);
  const [currenStep, setCurrenStep] = useState(-1);
  const [isConnectedSSE, setIsConnectedSSE, getIsConnectedSSE] = useGetState(false);
  const [detectStatus, setDetectStatus] = useState<DetectStatus>('Pending');
  const [deviceErrorStatus, setDeviceErrorStatus] = useState<DeviceErrorStatus>(null);
  const [btnLoadings, setBtnLoadings] = useSetState<Record<AllButtonType, boolean>>({} as any);
  const [isInReConnecting, setIsInReConnecting] = useState(false);
  const currentIsCompletePlayDeviceDetect = useRef(false);
  const eventSourceRef = useRef<SSE>(null);

  const time = useShowTime('星期dd&HH:mm');

  const { clear: clearChecking, onceCheckPass } = useCheckPingTimeout({
    timeoutMs: 6000,
    onCheckTimeout: () => {
      Toast.show('当前网络已断开，请稍后再试');
      setDeviceErrorStatus(
        currentIsCompletePlayDeviceDetect.current
          ? 'NetworkDisconnectInDetectMicDevice'
          : 'NetworkDisconnectInDetectPlayDevice'
      );
      eventSourceRef.current?.close?.();
      setIsConnectedSSE(false);
    },
  });

  const stepItems = useMemo<StepProps[]>(() => {
    switch (detectStatus) {
      case 'Pending':
        return [
          {
            title: '播放设备检测',
            description: <div className={cx('step-item')} />,
          },
          {
            title: '麦克风设备检测',
            description: null,
          },
        ];
      case 'DetectPlayDeviceSoftware':
        return [
          {
            title: '播放设备软件检测中...',
            description: (
              <div className={cx('step-item')}>
                <PlayDeviceDetecting type="software" />
              </div>
            ),
          },
          {
            title: '麦克风设备检测',
            description: null,
          },
        ];
      case 'DetectPlayDeviceHardware':
        return [
          {
            title: '播放设备硬件检测中...',
            description: (
              <div className={cx('step-item')}>
                <PlayDeviceDetecting type="hardware" />
              </div>
            ),
          },
          {
            title: '麦克风设备检测',
            description: null,
          },
        ];
      case 'PlayDeviceComplete':
        return [
          {
            title: '播放设备检测完成',
            description: (
              <div className={cx('detect-pass-tips')}>
                <Icon name="tips" className={cx('tips-icon')} />
                <span>确认已经听到2段音频播报</span>
              </div>
            ),
            icon: <Icon name="check" size={18} color="#fff" />,
          },
          {
            title: '麦克风设备检测',
            description: null,
          },
        ];
      case 'PrepareDetectMicDevice':
        return [
          {
            title: '播放设备检测完成',
            description: <div className={cx('step-item')} />,
            icon: <Icon name="check" size={18} color="#fff" />,
          },
          {
            title: '麦克风设备检测',
            description: null,
            status: 'wait',
          },
        ];
      case 'DetectMicDevice':
        return [
          {
            title: '播放设备检测完成',
            description: <div className={cx('step-item')} />,
            icon: <Icon name="check" size={18} color="#fff" />,
          },
          {
            title: '麦克风设备检测',
            description: (
              <div className={cx('step-item', 'mic-detect')}>
                <img className={cx('img')} src={VOICE_WAVE_IMG} alt="" />
                <span className={cx('mic-tips')}>可听到喇叭播放麦克风收音</span>
              </div>
            ),
          },
        ];
      case 'DetectMicDeviceComplete':
      case 'AllDeviceDetectComplete':
        return [
          {
            title: '播放设备检测完成',
            description: <div className={cx('step-item')} />,
            icon: <Icon name="check" size={18} color="#fff" />,
          },
          {
            title: '麦克风设备检测完成',
            description: null,
            icon: <Icon name="check" size={18} color="#fff" />,
          },
        ];
    }
  }, [detectStatus]);

  const handleClose = useCallback(() => {
    Dialog.confirm({
      title: '确认要退出检测吗？',
      content: '确认后将退出检测模式',
      confirmText: '确认退出',
      onConfirm: async () => {
        setIsDrawerOpen(false);
      },
    });
  }, []);

  const handleDetection = useCallback(
    async (deviceItem: DeviceItem, onConnected?: VoidFunc, onError?: NoopFunc) => {
      setDeviceInfo(deviceItem);

      try {
        const { data } = await service.device.getConnectionInfo(deviceItem?.deviceId);

        if (data?.host) {
          eventSourceRef.current = new SSE(`https://${data?.host}:${data?.port}/api/subscribe/audio_check`, {
            headers: {
              client_id: lookie.get(CURRENT_ANDROID_ID),
            },
          });
          // eventSourceRef.current = new SSE(`http://127.0.0.1:5050/sse`, { debug: true });

          eventSourceRef.current.onopen = e => {
            console.log('[SSE] open', e);
            setIsDrawerOpen(true);
            setIsConnectedSSE(true);
            onConnected?.();
            if (isNewSoftwareVersion(deviceItem?.softwareVersion, '1.8.0')) {
              onceCheckPass();
            }
          };

          eventSourceRef.current.onload = e => {
            console.log('[SSE] onload', e);
          };

          eventSourceRef.current.onabort = e => {
            console.log('[SSE] onabort', e);
            setIsConnectedSSE(false);
          };

          eventSourceRef.current.onerror = e => {
            onError?.();
            console.log('[SSE] onerror', e);

            let errMsg = getIsConnectedSSE() ? '当前网络已断开，请稍后再试' : '无法连接到机器人，请检查网络连接状态';

            try {
              const info = e?.data && JSON.parse(e.data);
              info?.code === 20000 && (errMsg = '当前设备正在进行检测，请稍后再试');
            } catch (e) {
              console.log(e);
            }
            if (getIsConnectedSSE()) {
              setDeviceErrorStatus(
                currentIsCompletePlayDeviceDetect.current
                  ? 'NetworkDisconnectInDetectMicDevice'
                  : 'NetworkDisconnectInDetectPlayDevice'
              );
            }

            Toast.show(errMsg);
            setIsConnectedSSE(false);
          };

          /** 通过ping检测是否与机器人断开连接 */
          if (isNewSoftwareVersion(deviceItem?.softwareVersion, '1.8.0')) {
            eventSourceRef.current.addEventListener('message', async () => {
              console.log('[SSE] message');
              onceCheckPass();
            });
          }

          /** 播放设备软件检测通过 */
          eventSourceRef.current.addEventListener('speaker_software_ok', async () => {
            console.log('[SSE] speaker_software_ok');
            await service.lanInterface.speakerCheck({
              deviceId: deviceItem?.deviceId,
              checkType: 'hardware',
            });
            setDetectStatus('DetectPlayDeviceHardware');
          });

          /** 播放设备软件检测异常 */
          eventSourceRef.current.addEventListener('speaker_software_error', async e => {
            console.log('[SSE] speaker_hardware_error', e);
            try {
              const errInfo = JSON.parse(e.data);
              switch (errInfo?.error) {
                case 'ServiceUnavailable':
                  setDeviceErrorStatus('PlayDeviceRunningException');
                  break;
                case 'DeviceNotFound':
                  setDeviceErrorStatus('PlayDeviceNotFound');
                  break;
                case 'DeviceBusy':
                  setDeviceErrorStatus('PlayDeviceBusy');
                  break;
                case 'OtherIssue':
                  setDeviceErrorStatus('PlayDeviceException');
                  break;
              }
            } catch (e) {
              console.error(e);
            }
          });

          /** 播放设备硬件检测通过 */
          eventSourceRef.current.addEventListener('speaker_hardware_ok', async () => {
            console.log('[SSE] speaker_hardware_ok');
            setDetectStatus('PlayDeviceComplete');
            currentIsCompletePlayDeviceDetect.current = true;
          });

          /** 播放设备硬件检测异常 */
          eventSourceRef.current.addEventListener('speaker_hardware_error', async e => {
            console.log('[SSE] speaker_hardware_error', e);
            try {
              const errInfo = JSON.parse(e.data);
              switch (errInfo?.error) {
                case 'ServiceUnavailable':
                  setDeviceErrorStatus('PlayDeviceRunningException');
                  break;
                case 'DeviceNotFound':
                  setDeviceErrorStatus('PlayDeviceNotFound');
                  break;
                case 'DeviceBusy':
                  setDeviceErrorStatus('PlayDeviceBusy');
                  break;
                case 'OtherIssue':
                  setDeviceErrorStatus('PlayDeviceException');
                  break;
              }
            } catch (e) {
              console.error(e);
            }
          });

          /** 麦克风检测通过 */
          eventSourceRef.current.addEventListener('microphone_ok', async () => {
            console.log('[SSE] microphone_ok');
            setDetectStatus('DetectMicDeviceComplete');
            setCurrenStep(2);
          });

          /** 麦克风检测异常 */
          eventSourceRef.current.addEventListener('microphone_error', async e => {
            console.log('[SSE] microphone_error', e);
            try {
              const errInfo = JSON.parse(e.data);
              switch (errInfo?.error) {
                case 'ServiceUnavailable':
                  setDeviceErrorStatus('MicRunningException');
                  break;
                case 'DeviceNotFound':
                  setDeviceErrorStatus('MicNotFound');
                  break;
                case 'DeviceBusy':
                  setDeviceErrorStatus('MicBusy');
                  break;
                case 'OtherIssue':
                  setDeviceErrorStatus('MicRunningException');
                  break;
              }
            } catch (e) {
              console.error(e);
            }
          });
        }
      } catch (e) {
        onError?.(e);
      }
    },
    [getIsConnectedSSE, onceCheckPass, setIsConnectedSSE]
  );

  /** 播放设备软件监测 */
  const handleSpeakerCheckSoftware = useLockFn(
    useCallback(async () => {
      try {
        setBtnLoadings({ detectPlayDeviceSoftware: true });
        await service.lanInterface.speakerCheck({
          deviceId: deviceInfo?.deviceId,
          checkType: 'software',
        });
        setCurrenStep(0);
        setDetectStatus('DetectPlayDeviceSoftware');
      } finally {
        setBtnLoadings({ detectPlayDeviceSoftware: false });
      }
    }, [deviceInfo?.deviceId, setBtnLoadings])
  );

  // 麦克风设备检测
  const handleMicrophoneCheck = useLockFn(
    useCallback(async () => {
      try {
        setBtnLoadings({ detectMicDevice: true });
        await service.lanInterface.microphoneCheck({
          deviceId: deviceInfo?.deviceId,
          duration: 10,
        });
        setCurrenStep(1);
        setDetectStatus('DetectMicDevice');
      } finally {
        setBtnLoadings({ detectMicDevice: false });
      }
    }, [deviceInfo?.deviceId, setBtnLoadings])
  );

  const renderFooterBtns = useMemo(() => {
    // 检测发生了异常
    if (deviceErrorStatus) {
      switch (deviceErrorStatus) {
        case 'PlayDeviceException':
        case 'PlayDeviceRunningException':
        case 'PlayDeviceBusy':
        case 'PlayDeviceNotFound':
        case 'MicBusy':
        case 'MicNotFound':
        case 'MicRunningException':
        case 'NetworkDisconnectInDetectMicDevice':
        case 'NetworkDisconnectInDetectPlayDevice':
          return (
            <>
              <Button color="danger" onClick={handleClose} className={cx('btn', 'btn-default')}>
                退出检测
              </Button>
              <Button
                loading={isInReConnecting}
                color="primary"
                className={cx('btn')}
                onClick={() => {
                  if (!isConnectedSSE) {
                    setIsInReConnecting(true);
                    eventSourceRef.current?.close?.();
                    handleDetection(
                      deviceInfo,
                      () => {
                        setCurrenStep(-1);
                        setDeviceErrorStatus(null);
                        setDetectStatus('Pending');
                        setIsInReConnecting(false);
                      },
                      () => {
                        setIsInReConnecting(false);
                      }
                    );
                  } else {
                    setCurrenStep(-1);
                    setDeviceErrorStatus(null);
                    setDetectStatus('Pending');
                  }
                  currentIsCompletePlayDeviceDetect.current = false;
                }}
              >
                再次检测
              </Button>
            </>
          );
      }
    } else {
      switch (detectStatus) {
        case 'Pending':
          return (
            <Button
              color="primary"
              disabled={!isConnectedSSE}
              loading={btnLoadings?.detectPlayDeviceSoftware}
              className={cx('btn')}
              onClick={handleSpeakerCheckSoftware}
            >
              开始检测
            </Button>
          );
        /** 播放设备软件/硬件监测 */
        case 'DetectPlayDeviceSoftware':
        case 'DetectPlayDeviceHardware':
          return (
            <Button disabled color="primary" className={cx('btn')}>
              设备检测中
            </Button>
          );
        /** 播放设备检测完成 */
        case 'PlayDeviceComplete':
          return (
            <>
              <Button
                color="danger"
                onClick={() => {
                  setDeviceErrorStatus('PlayDeviceException');
                }}
                className={cx('btn', 'btn-default')}
              >
                没听到音频
              </Button>
              <Button
                loading={btnLoadings?.detectMicDevice}
                onClick={() => {
                  setDetectStatus('PrepareDetectMicDevice');
                  setCurrenStep(1);
                }}
                color="primary"
                className={cx('btn')}
              >
                确认听到
              </Button>
            </>
          );
        case 'PrepareDetectMicDevice':
          return (
            <Button
              color="primary"
              loading={btnLoadings?.detectMicDevice}
              className={cx('btn')}
              onClick={handleMicrophoneCheck}
            >
              开始检测
            </Button>
          );
        /** 麦克风设备检测 */
        case 'DetectMicDevice':
          return <ButtonWithCountdown />;
        /** 完成麦克风设备检测 */
        case 'DetectMicDeviceComplete':
          return (
            <>
              <Button
                color="danger"
                onClick={() => {
                  setDeviceErrorStatus('MicRunningException');
                }}
                className={cx('btn', 'btn-default')}
              >
                没听到音频
              </Button>
              <Button
                onClick={() => {
                  setCurrenStep(2);
                  setDetectStatus('AllDeviceDetectComplete');
                }}
                color="primary"
                className={cx('btn')}
              >
                确认听到
              </Button>
            </>
          );
        /** 所有设备检测完成 */
        case 'AllDeviceDetectComplete':
          return (
            <>
              <Button color="danger" onClick={handleClose} className={cx('btn', 'btn-default')}>
                退出检测
              </Button>
              <Button
                color="primary"
                className={cx('btn')}
                onClick={() => {
                  setDetectStatus('Pending');
                  setCurrenStep(-1);
                  setDeviceErrorStatus(null);
                  currentIsCompletePlayDeviceDetect.current = false;
                }}
              >
                再次检测
              </Button>
            </>
          );
        default:
          return null;
      }
    }
  }, [
    btnLoadings,
    detectStatus,
    deviceErrorStatus,
    deviceInfo,
    isInReConnecting,
    handleClose,
    handleDetection,
    handleMicrophoneCheck,
    handleSpeakerCheckSoftware,
    isConnectedSSE,
  ]);

  // 当一键清除播放设备忙
  const onClearBusyPlayDevice = useCallback(() => {
    setDeviceErrorStatus(null);
    setDetectStatus('Pending');
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      handleDetection,
    }),
    [handleDetection]
  );

  useUpdateEffect(() => {
    if (isDrawerOpen) {
      console.log('detecting...');
    } else {
      eventSourceRef.current?.close();
      setCurrenStep(-1);
      setDetectStatus('Pending');
      setDeviceErrorStatus(null);
      currentIsCompletePlayDeviceDetect.current = false;
      clearChecking();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDrawerOpen, deviceInfo]);

  return (
    <Drawer
      placement="right"
      width={'520px'}
      className={cx('video-drawer-voice-detection')}
      styles={{
        header: { border: 'none' },
        body: { backgroundColor: '#E6E8EB' },
      }}
      closable={false}
      destroyOnClose={true}
      open={isDrawerOpen}
    >
      <div className={cx('header')}>
        <Icon size={28} name="close-drawer" onClick={handleClose} />
        <div className={cx['time-text']}>
          <div className={cx('week')}>{time?.split('&')[0]}</div>
          <div className={cx('line')}>|</div>
          <div className={cx('minute')}>{time?.split('&')[1]}</div>
        </div>
      </div>
      <div className={cx('content')}>
        <div className={cx('device-info')}>
          <div className={cx('name')}>{deviceInfo?.deviceName}</div>
          <div className={cx('sub-info')}>
            <div className={cx('device-id')}>{deviceInfo?.deviceId}</div>
            <div className={cx('device-version')}>
              {!!deviceInfo?.softwareVersion && `V${deviceInfo.softwareVersion}`}
            </div>
          </div>
        </div>
        <div className={cx('steps-wrapper')}>
          {deviceErrorStatus ? (
            <DetectError
              deviceInfo={deviceInfo}
              errorState={deviceErrorStatus}
              onClearBusyPlayDevice={onClearBusyPlayDevice}
            />
          ) : (
            <Steps direction="vertical" current={currenStep} items={stepItems} />
          )}
        </div>
      </div>
      <div className={cx('btns')}>{renderFooterBtns}</div>
    </Drawer>
  );
});

export default VoiceDetection;
